/**
 * Shared presentational helpers for the visual editor canvas — extracted from
 * visual-style-file-page.tsx so every tab can reuse them without duplication.
 */
import { useRef } from "react";
import { Camera } from "lucide-react";
import { Button } from "@flint/ui";

/** Overlay banner for empty canvas states ("No defects pinned yet…"). */
export function CanvasEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="absolute inset-x-4 bottom-3 z-20 rounded-sm border border-dashed border-line bg-paper/80 px-3 py-2 text-center text-xs text-muted backdrop-blur-sm">
      {children}
    </div>
  );
}

/** Stat card — DHU, RFT, etc. */
export function Tile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub: string;
}) {
  return (
    <div className="rounded-md border border-line bg-bg p-2.5">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">
        {label}
      </div>
      <div className="mt-0.5 font-mono text-lg font-bold text-ink">{value}</div>
      <div className="text-xs text-muted">{sub}</div>
    </div>
  );
}

/** Key-value row for inspector detail lists. */
export function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{k}</dt>
      <dd className="text-right font-medium text-ink-2">{v}</dd>
    </div>
  );
}

/** Full-height centred loading / error text. */
export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-[40vh] place-items-center text-sm text-muted">
      {children}
    </div>
  );
}

/** Empty-state placeholder for a panel (title + dashed box). */
export function Placeholder({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      <div className="rounded-sm border border-dashed border-line bg-bg px-3 py-4 text-xs leading-relaxed text-muted">
        {body}
      </div>
    </div>
  );
}

/** Compute the tolerance bar's zone and dot positions as percentages. */
export function computeToleranceBarPositions(
  specMm: number,
  tolMm: number,
  actualMm: number,
): { zoneLeft: number; zoneWidth: number; dotPct: number } {
  // Display range = spec ± 2×tol so out-of-range values are still visible
  const range = tolMm * 2 || specMm * 0.1 || 10; // fallback if tol is 0
  const lo = specMm - range;
  const hi = specMm + range;
  const span = hi - lo;

  // Green zone band (spec ± tol) as % of display range
  const zoneLeft = ((specMm - tolMm - lo) / span) * 100;
  const zoneWidth = ((tolMm * 2) / span) * 100;

  // Dot position — clamp so the dot stays within the track
  const rawDotPct = ((actualMm - lo) / span) * 100;
  const dotPct = Math.max(2, Math.min(98, rawDotPct));

  return { zoneLeft, zoneWidth, dotPct };
}

/** Horizontal tolerance bar — green zone centered at spec ± tol, dot at actual. */
export function ToleranceBar({
  specMm,
  tolMm,
  actualMm,
  inTolerance,
}: {
  specMm: number;
  tolMm: number;
  actualMm: number;
  inTolerance: boolean;
}) {
  const { zoneLeft, zoneWidth, dotPct } = computeToleranceBarPositions(
    specMm,
    tolMm,
    actualMm,
  );

  return (
    <div className="relative h-2 w-full overflow-hidden rounded-full bg-bg">
      {/* Green zone band */}
      <div
        className="absolute inset-y-0 rounded-full bg-emerald-500/20"
        style={{ left: `${zoneLeft}%`, width: `${zoneWidth}%` }}
      />
      {/* Actual marker dot */}
      <div
        className={`absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-sm ${inTolerance ? "bg-emerald-500" : "bg-bad"}`}
        style={{ left: `${dotPct}%` }}
      />
    </div>
  );
}

/** Photo upload slot — shows current photo or "No photo yet", with upload button. */
export function PhotoSlot({
  photoUrl,
  uploading,
  onUpload,
}: {
  photoUrl: string | null;
  uploading: boolean;
  onUpload: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-col gap-2">
      {photoUrl ? (
        <a
          href={photoUrl}
          target="_blank"
          rel="noreferrer"
          className="block overflow-hidden rounded-md border border-line"
        >
          <img
            src={photoUrl}
            alt="Defect photo"
            className="h-40 w-full object-cover"
          />
        </a>
      ) : (
        <div className="grid h-20 place-items-center rounded-md border border-dashed border-line bg-bg text-xs text-muted">
          No photo yet
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.currentTarget.files?.[0];
          if (f) onUpload(f);
          e.currentTarget.value = "";
        }}
      />
      <Button
        size="sm"
        intent="ghost"
        className="border border-line"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        <Camera size={14} />{" "}
        {uploading ? "Uploading…" : photoUrl ? "Replace photo" : "Attach photo"}
      </Button>
    </div>
  );
}
